import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import InutilizacaoTable, {
    inutilizacaoTableColumns,
} from "components/tables/InutilizacaoTable";
import { inutilizacoesBreadcrumbItems } from "data/inutilizacao";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { useEmpresas } from "providers/EmpresaProvider";
import { ChangeEvent, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useDebounce from "hooks/useDebounce";
import InutilizacaoMenus, { InutilizacaoFilter } from "./menus";
import qs from "qs";

type Pagina<T> = {
    total: number;
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    itens?: T[];
    data?: T[];
};

const fetchInutilizacoes = async ({
    pageIndex,
    pageSize,
    filter,
    sort,
    menuFilter,
    empresasIds,
}: {
    pageIndex: number;
    pageSize: number;
    filter?: string;
    sort?: SortingState;
    menuFilter?: InutilizacaoFilter;
    empresasIds?: number[];
}) => {
    const { data } = await axios.get("/api/inutilizacao", {
        params: {
            pageSize,
            pageIndex: pageIndex + 1, // Backend usa 1-based pagination
            ...menuFilter,
            empresasIds: empresasIds,
            sort:
                sort && sort.length > 0
                    ? `${sort[0].id},${sort[0].desc ? "desc" : "asc"}`
                    : undefined,
        },
        paramsSerializer: (params) => qs.stringify(params, { indices: false }),
    });
    return data;
};

const Inutilizacoes = () => {
    const { selectedIds } = useEmpresas();
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    const [sort, setSort] = useState<SortingState>(!keepPreviousData ? [] : []);
    const [filter, setFilter] = useState<string | undefined>();
    const [menuFilter, setMenuFilter] = useState<InutilizacaoFilter>({});
    const debouncedFilter = useDebounce(filter, 300);

    const dataQuery = useQuery({
        queryKey: [
            "inutilizacoes",
            pagination,
            debouncedFilter,
            sort,
            menuFilter,
            selectedIds,
        ],
        queryFn: () =>
            fetchInutilizacoes({
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
                filter,
                sort,
                menuFilter,
                empresasIds: selectedIds,
            }),
        placeholderData: keepPreviousData,
    });

    const table = useReactTable({
        data: dataQuery.data?.data ?? dataQuery.data?.itens ?? [],
        columns: inutilizacaoTableColumns,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getCoreRowModel: getCoreRowModel(),
        onPaginationChange: setPagination,
        onGlobalFilterChange: setFilter,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        onSortingChange: (updateOrValue: Updater<SortingState>) => {
            if (typeof updateOrValue === "function") {
                setSort((prev) => {
                    const updated = updateOrValue(prev);
                    if (prev[0]?.id === updated[0]?.id) {
                        updated[0].desc = !prev[0]?.desc;
                    }
                    return updated;
                });
            } else {
                setSort(updateOrValue);
            }
        },
        pageCount: dataQuery.data?.totalPages ?? 0,
        state: {
            globalFilter: "",
            pagination: {
                pageIndex: (dataQuery.data?.pageIndex ?? 1) - 1, // Convert to 0-based
                pageSize: dataQuery.data?.pageSize ?? 10,
            },
        },
        initialState: {
            pagination: {
                pageIndex: (dataQuery.data?.pageIndex ?? 1) - 1,
                pageSize: dataQuery.data?.pageSize ?? 10,
            },
        },
    });

    if (dataQuery.isLoading)
        return (
            <Spinner animation="border" role="status" className="d-block mx-auto">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        );

    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    return (
        <div>
            <PageBreadcrumb items={inutilizacoesBreadcrumbItems} />
            <div className="mb-9">
                <h2 className="mb-5">Inutilizações</h2>

                <AdvanceTableProvider {...table}>
                    <div className="mb-4">
                        <div className="d-flex flex-wrap gap-3">
                            <SearchBox
                                placeholder="Pesquisar inutilizações"
                                onChange={handleSearchInputChange}
                            />
                            <InutilizacaoMenus
                                filters={menuFilter}
                                onFilter={setMenuFilter}
                            />
                            <div className="ms-xxl-auto">
                                <Button
                                    variant="primary"
                                    as={Link}
                                    to="/operacional/inutilizacao/nova"
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Nova Inutilização
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                        <InutilizacaoTable />
                    </div>
                </AdvanceTableProvider>
            </div>
        </div>
    );
};

export default Inutilizacoes; 